import React, { useEffect, useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { Form, FormGroup } from 'reactstrap';
import { Button } from '@shoflo/polymer/components/Button/Button';
import { Input } from '@shoflo/polymer/components/Input/Input';
import { InputLabel } from '@shoflo/polymer/components/InputLabel/InputLabel';
import { UserValidator } from '@shoflo/polymer/validators/UserValidator/UserValidator';
import { Alert } from '@shoflo/polymer/components/Alert/Alert';
import ModalPage from 'containers/pages/ModalPage/ModalPage';
import ModalFooterLink from '../../../components/Links/ModalFooterLink';
import ModalSubHeader from 'components/ModalSubHeader';
import AppPaths from '../../../AppPaths';

import { createPasswordRecoveryToken } from '../../../actions/user';

export const ForgotPasswordPageUnConnected = ({ sendRecoveryEmail }) => {
  const subHeaderText = `Enter email address below and if we find an email that matches the one you provided, you'll receive a forgot password link shortly. If you never receive it, it's likely that you don't have an account or it's with a different email address. Use the blue support chat below if you need help.`;

  const [email, setEmail] = useState('');
  const [error, setError] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const buttonIsInactive = !!error || !email;

  const emailIsValid = useMemo(() => {
    return UserValidator.email(email);
  }, [email]);

  useEffect(() => {
    if (email) {
      setError(
        emailIsValid
          ? null
          : {
              type: 'error',
              message: 'Invalid email address'
            }
      );
    }
  }, [email, emailIsValid]);

  const onChange = e => {
    const { value } = e.target;
    setEmail(value.trim());
  };

  const handleSubmit = () => {
    sendRecoveryEmail(email);
    setIsSubmitted(true);
  };

  return (
    <ModalPage
      header="Forgot password"
      subHeader={
        !isSubmitted ? <ModalSubHeader message={subHeaderText} /> : null
      }
      actionButton={<></>}
      footer={
        <ModalFooterLink to={AppPaths.LoginPage}>
          Go back to sign in
        </ModalFooterLink>
      }
    >
      <>
        {!isSubmitted && (
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <InputLabel for="email">Email address</InputLabel>
              <Input
                id="email"
                placeholder="e.g. tim.berg@gmail.com"
                value={email}
                error={error}
                onChange={onChange}
              />
            </FormGroup>
            <div className="justify-center">
              <Button color="primary" disabled={buttonIsInactive} type="submit">
                Send reset link
              </Button>
            </div>
          </Form>
        )}
        {isSubmitted && (
          <Alert
            type="success"
            icon={faInfoCircle}
            title={`Password reset link has been sent to ${email}`}
            content="Again, if you never receive it, it's likely that you do not have an account yet or it is registered under a different email address."
            footer="Use the blue support chat below if you need help."
          />
        )}
      </>
    </ModalPage>
  );
};

ForgotPasswordPageUnConnected.propTypes = {
  sendRecoveryEmail: PropTypes.func
};

const mapDispatchToProps = dispatch => {
  return {
    sendRecoveryEmail: (email) =>
      dispatch(createPasswordRecoveryToken(email))
  };
};

export default connect(null, mapDispatchToProps)(ForgotPasswordPageUnConnected);
